import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useRegisterRefresh } from '../context/PullToRefreshContext';
import TaskCard from '../components/TaskCard';

const emptyContact = { title: '', first_name: '', last_name: '', phone: '', email: '' };
const PAGE_SIZE = 10;

function isValidPhone(phone) {
  const digits = phone.replace(/[\s+\-()/]/g, '');
  return /^\d{7,15}$/.test(digits);
}
function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

// ─── Kişi formu (hem ekleme hem düzenleme için) ───────────────────────────────
function ContactForm({ initial, onSave, onCancel, saving, error }) {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      {error && <p className="text-sm text-red-600">{error}</p>}
      <input className="input" style={{ fontSize: '16px' }} placeholder="Unvan" value={form.title} onChange={set('title')} />
      <input className="input" style={{ fontSize: '16px' }} placeholder="Ad *" value={form.first_name} onChange={set('first_name')} />
      <input className="input" style={{ fontSize: '16px' }} placeholder="Soyad *" value={form.last_name} onChange={set('last_name')} />
      <div>
        <input
          className="input"
          style={{ fontSize: '16px' }}
          placeholder="Telefon (örn: 05551234567)"
          value={form.phone}
          onChange={set('phone')}
        />
        {form.phone.trim() && !isValidPhone(form.phone) && (
          <p className="text-xs text-red-500 mt-0.5">Geçersiz telefon formatı.</p>
        )}
      </div>
      <div>
        <input className="input" style={{ fontSize: '16px' }} placeholder="E-posta" value={form.email} onChange={set('email')} />
        {form.email.trim() && !isValidEmail(form.email) && (
          <p className="text-xs text-red-500 mt-0.5">Geçersiz e-posta adresi.</p>
        )}
      </div>
      <p className="text-xs text-gray-400">* Telefon veya e-posta bilgilerinden en az biri zorunludur.</p>
      <div className="flex gap-2 pt-1">
        <button type="button" className="btn-secondary" onClick={onCancel}>Vazgeç</button>
        <button type="submit" className="btn-primary" disabled={saving}>
          {saving ? '…' : 'Kaydet'}
        </button>
      </div>
    </form>
  );
}

// ─── Klasör satırı ────────────────────────────────────────────────────────────
function FolderRow({ label, count, open, onClick, indent = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center w-full text-left px-3 py-2 rounded-xl hover:bg-gray-50 transition-colors ${
        indent ? 'pl-7' : ''
      }`}
    >
      <span className="mr-2 text-gray-400 text-xs w-3">{open ? '▼' : '▶'}</span>
      <span className={`flex-1 text-sm font-medium ${indent ? 'text-gray-600' : 'text-gray-800'}`}>
        {label}
      </span>
      <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{count}</span>
    </button>
  );
}

export default function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // ── Veri ──────────────────────────────────────────────────────────────────
  const [customer, setCustomer] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ── Müşteri adı düzenleme ─────────────────────────────────────────────────
  const [editingName, setEditingName] = useState(false);
  const [editName, setEditName] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameError, setNameError] = useState('');

  // ── Müşteri türü düzenleme ────────────────────────────────────────────────
  const [editingType, setEditingType] = useState(false);
  const [editType, setEditType] = useState('');
  const [typeSaving, setTypeSaving] = useState(false);
  const [typeOptions, setTypeOptions] = useState([]);
  const [pendingTypeRequest, setPendingTypeRequest] = useState(null);
  // Yeni tür talebi formu
  const [showNewTypeForm, setShowNewTypeForm] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeError, setNewTypeError] = useState('');
  const [newTypeSaving, setNewTypeSaving] = useState(false);

  // ── Piyasa (is_domestic) — sadece admin ───────────────────────────────────
  const [editingMarket, setEditingMarket] = useState(false);
  const [editIsDomestic, setEditIsDomestic] = useState(null);
  const [marketSaving, setMarketSaving] = useState(false);

  // ── Kişi ekleme ───────────────────────────────────────────────────────────
  const [showAddContact, setShowAddContact] = useState(false);
  const [addContactSaving, setAddContactSaving] = useState(false);
  const [addContactError, setAddContactError] = useState('');

  // ── Kişi düzenleme (admin) ────────────────────────────────────────────────
  const [editingContactId, setEditingContactId] = useState(null);
  const [editContactSaving, setEditContactSaving] = useState(false);
  const [editContactError, setEditContactError] = useState('');

  // ── İşler — ağaç / arama / sayfalama ─────────────────────────────────────
  const [search, setSearch] = useState('');
  const [openFolders, setOpenFolders] = useState(new Set(['__all__'])); // '__all__' veya 'catName' veya 'catName|||year'
  const [page, setPage] = useState(0);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const [{ data: c }, { data: t }, { data: typeData }] = await Promise.all([
        api.getCustomer(id),
        api.getTasks({ customer: id }),
        api.getCustomerTypeOptions(),
      ]);
      setCustomer(c);
      setTasks(Array.isArray(t) ? t : t.results || []);
      setTypeOptions(typeData.options || []);
      setPendingTypeRequest(typeData.pending_request || null);
    } catch {
      setError('Veriler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Arama değişince sayfayı sıfırla
  useEffect(() => { setPage(0); }, [search]);

  // ── Müşteri türü kaydet ───────────────────────────────────────────────────
  const saveType = async (val) => {
    if (!val) return;
    setTypeSaving(true);
    try {
      await api.updateCustomer(id, { customer_type: val });
      setEditingType(false);
      await load();
    } catch { /* sessiz hata */ }
    finally { setTypeSaving(false); }
  };

  const submitNewTypeRequest = async () => {
    const name = newTypeName.trim();
    if (!name) { setNewTypeError('Tür adı boş olamaz.'); return; }
    setNewTypeSaving(true); setNewTypeError('');
    try {
      await api.createCustomerTypeRequest(name);
      setShowNewTypeForm(false);
      setNewTypeName('');
      setEditingType(false);
      await load();
    } catch (err) {
      setNewTypeError(err.response?.data?.detail || 'Talep oluşturulamadı.');
    } finally {
      setNewTypeSaving(false);
    }
  };

  const saveName = async () => {
    if (!editName.trim()) { setNameError('Müşteri adı boş olamaz.'); return; }
    setNameSaving(true); setNameError('');
    try {
      await api.updateCustomer(id, { name: editName.trim() });
      setEditingName(false);
      await load();
    } catch { setNameError('Ad güncellenemedi.'); }
    finally { setNameSaving(false); }
  };

  const saveMarket = async () => {
    if (editIsDomestic !== true && editIsDomestic !== false) return;
    setMarketSaving(true);
    try {
      await api.updateCustomer(id, { is_domestic: editIsDomestic });
      setEditingMarket(false);
      await load();
    } catch { /* sessiz */ }
    finally { setMarketSaving(false); }
  };

  // ── Kişi ekle ─────────────────────────────────────────────────────────────
  const submitAddContact = async (form) => {
    setAddContactError('');
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setAddContactError('Ad ve soyad zorunludur.'); return;
    }
    if (!form.phone.trim() && !form.email.trim()) {
      setAddContactError('Telefon veya e-posta bilgilerinden en az biri zorunludur.'); return;
    }
    if (form.phone.trim() && !isValidPhone(form.phone)) {
      setAddContactError('Geçersiz telefon formatı.'); return;
    }
    if (form.email.trim() && !isValidEmail(form.email)) {
      setAddContactError('Geçersiz e-posta adresi.'); return;
    }
    setAddContactSaving(true);
    try {
      await api.createContact(id, form);
      setShowAddContact(false);
      await load();
    } catch { setAddContactError('Kişi eklenemedi.'); }
    finally { setAddContactSaving(false); }
  };

  // ── Kişi düzenle (admin) ──────────────────────────────────────────────────
  const submitEditContact = async (form) => {
    setEditContactError('');
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setEditContactError('Ad ve soyad zorunludur.'); return;
    }
    if (!form.phone.trim() && !form.email.trim()) {
      setEditContactError('Telefon veya e-posta bilgilerinden en az biri zorunludur.'); return;
    }
    if (form.phone.trim() && !isValidPhone(form.phone)) {
      setEditContactError('Geçersiz telefon formatı.'); return;
    }
    if (form.email.trim() && !isValidEmail(form.email)) {
      setEditContactError('Geçersiz e-posta adresi.'); return;
    }
    setEditContactSaving(true);
    try {
      await api.updateContact(id, editingContactId, form);
      setEditingContactId(null);
      await load();
    } catch { setEditContactError('Kişi güncellenemedi.'); }
    finally { setEditContactSaving(false); }
  };

  // ── Kişi sil (admin) ──────────────────────────────────────────────────────
  const deleteContact = async (contact) => {
    if (!window.confirm(`"${contact.first_name} ${contact.last_name}" kişisini silmek istiyor musunuz?`)) return;
    try {
      await api.deleteContact(id, contact.id);
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Kişi silinemedi.');
    }
  };

  // ── Klasör toggle ─────────────────────────────────────────────────────────
  const toggleFolder = (key) => {
    setOpenFolders((prev) => {
      const next = new Set(prev);
      if (key === '__all__') {
        // Tümü açılıyorsa tüm kategori/yıl klasörlerini kapat
        [...next].filter((k) => k !== '__all__').forEach((k) => next.delete(k));
        if (next.has('__all__')) next.delete('__all__'); else next.add('__all__');
      } else if (!key.includes('|||')) {
        // Kategori klasörü: Tümü'yü kapat
        next.delete('__all__');
        if (next.has(key)) next.delete(key); else next.add(key);
      } else {
        // Yıl alt klasörü: sadece toggle
        if (next.has(key)) next.delete(key); else next.add(key);
      }
      return next;
    });
    if (key === '__all__') setPage(0);
  };

  // ── Arama filtresi ────────────────────────────────────────────────────────
  const filteredTasks = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tasks;
    // Boşluk, /, -, nokta, virgül vb. ayraçlara göre tokenla.
    // "100 ton/ay" → ["100","ton","ay"] — hepsi metinde geçmeli (sıra fark etmez).
    const tokens = q.split(/[\s/\-.,;:!?()[\]]+/).filter(Boolean);
    return tasks.filter((t) => {
      const haystack = (t.title + ' ' + (t.process_notes_text || '') + ' ' + (t.output_text || '')).toLowerCase();
      return tokens.every((token) => haystack.includes(token));
    });
  }, [tasks, search]);

  // ── Kategori → Yıl ağacı (sadece içeriği olanlar) ────────────────────────
  const catTree = useMemo(() => {
    const tree = {};
    filteredTasks.forEach((t) => {
      const cat  = t.category_name || 'Diğer';
      const year = t.created_at ? String(new Date(t.created_at).getFullYear()) : 'Bilinmiyor';
      if (!tree[cat]) tree[cat] = {};
      if (!tree[cat][year]) tree[cat][year] = [];
      tree[cat][year].push(t);
    });
    return tree;
  }, [filteredTasks]);

  // ── "Tümü" — son güncellenen, sayfalı ────────────────────────────────────
  const allSorted = useMemo(
    () => [...filteredTasks].sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0)),
    [filteredTasks],
  );
  const totalPages  = Math.ceil(allSorted.length / PAGE_SIZE);
  const currentPage = Math.min(page, Math.max(0, totalPages - 1));
  const pageItems   = allSorted.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

  // ─────────────────────────────────────────────────────────────────────────
  if (loading && !customer) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-7 h-7 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (error || !customer) {
    return <div className="px-4 py-8 text-center text-sm text-red-600">{error || 'Müşteri bulunamadı.'}</div>;
  }

  const contacts = customer.contacts || [];

  return (
    <div className="px-4 py-3 pb-24 space-y-4">
      {/* Geri */}
      <button type="button" className="text-sm text-brand-600 font-medium" onClick={() => navigate('/satis/customers')}>
        ← Müşteriler
      </button>

      {/* ── Müşteri adı ── */}
      {editingName ? (
        <div className="space-y-1">
          <input
            className="input text-lg font-bold"
            style={{ fontSize: '16px' }}
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            autoFocus
          />
          {nameError && <p className="text-xs text-red-500">{nameError}</p>}
          <div className="flex gap-2">
            <button type="button" className="btn-primary text-sm" onClick={saveName} disabled={nameSaving}>
              {nameSaving ? 'Kaydediliyor…' : 'Kaydet'}
            </button>
            <button type="button" className="btn-secondary text-sm" onClick={() => setEditingName(false)}>İptal</button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-bold text-gray-900 flex-1">{customer.name}</h1>
          {isAdmin && (
            <>
              <button type="button" className="text-xs text-brand-600 font-medium shrink-0"
                onClick={() => { setEditName(customer.name); setNameError(''); setEditingName(true); }}>
                Düzenle
              </button>
              <button type="button" className="text-xs text-red-500 font-medium shrink-0"
                onClick={async () => {
                  if (!window.confirm(`"${customer.name}" müşterisini silmek istediğinize emin misiniz?`)) return;
                  try { await api.deleteCustomer(id); navigate('/satis/customers'); }
                  catch (err) { alert(err.response?.data?.detail || 'Müşteri silinemedi.'); }
                }}>
                Sil
              </button>
            </>
          )}
        </div>
      )}

      {/* ── Müşteri türü ── */}
      <div className="space-y-2">
        {editingType ? (
          <div className="space-y-2">
            {!showNewTypeForm ? (
              <>
                <select
                  className="input flex-1"
                  style={{ fontSize: '16px' }}
                  value={editType}
                  onChange={(e) => {
                    if (e.target.value === '__new__') {
                      setShowNewTypeForm(true);
                      setEditType('');
                    } else {
                      setEditType(e.target.value);
                    }
                  }}
                  autoFocus
                >
                  <option value="">— Tür seçin —</option>
                  {typeOptions.map((o) => (
                    <option key={o.code} value={o.code}>{o.name}</option>
                  ))}
                  <option value="__new__">+ Yeni tür talep et…</option>
                </select>
                <div className="flex gap-2">
                  <button type="button" className="btn-primary text-xs !w-auto px-3 shrink-0" onClick={() => saveType(editType)} disabled={typeSaving || !editType}>
                    {typeSaving ? '…' : 'Kaydet'}
                  </button>
                  <button type="button" className="btn-secondary text-xs !w-auto px-3 shrink-0" onClick={() => setEditingType(false)}>İptal</button>
                </div>
              </>
            ) : (
              <div className="card space-y-2">
                <p className="text-xs font-semibold text-gray-700">Yeni müşteri türü talebi</p>
                <p className="text-xs text-gray-500">Admin onayladıktan sonra kullanılabilir hale gelecektir.</p>
                <input
                  className="input"
                  style={{ fontSize: '16px' }}
                  placeholder="Tür adı (örn: Distribütör)"
                  value={newTypeName}
                  onChange={(e) => setNewTypeName(e.target.value)}
                  autoFocus
                />
                {newTypeError && <p className="text-xs text-red-600">{newTypeError}</p>}
                <div className="flex gap-2">
                  <button type="button" className="btn-primary text-xs !w-auto px-3 shrink-0" onClick={submitNewTypeRequest} disabled={newTypeSaving || !newTypeName.trim()}>
                    {newTypeSaving ? '…' : 'Talep Gönder'}
                  </button>
                  <button type="button" className="btn-secondary text-xs !w-auto px-3 shrink-0" onClick={() => { setShowNewTypeForm(false); setNewTypeName(''); setNewTypeError(''); }}>
                    Geri
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            {customer.customer_type ? (
              <span className="text-xs font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full bg-brand-50 text-brand-700 border border-brand-200">
                {customer.customer_type}
              </span>
            ) : (
              <span className="text-xs text-gray-400">Tür belirtilmemiş</span>
            )}
            <button type="button" className="text-xs text-brand-600 font-medium"
              onClick={() => { setEditType(customer.customer_type || ''); setEditingType(true); setShowNewTypeForm(false); }}>
              {customer.customer_type ? 'Değiştir' : 'Tür Ekle'}
            </button>
            {pendingTypeRequest && (
              <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                "{pendingTypeRequest.name}" onay bekliyor
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Piyasa (yurtiçi / yurtdışı) ── */}
      <div className="space-y-2">
        {editingMarket ? (
          <div className="space-y-2">
            <p className="text-xs font-medium text-gray-700">Piyasa</p>
            <div className="flex rounded-xl border border-gray-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setEditIsDomestic(true)}
                className={`flex-1 py-2.5 px-2 text-sm font-medium border-r border-gray-200 transition-colors
                  ${editIsDomestic === true ? 'bg-brand-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                Yurtiçi
              </button>
              <button
                type="button"
                onClick={() => setEditIsDomestic(false)}
                className={`flex-1 py-2.5 px-2 text-sm font-medium transition-colors
                  ${editIsDomestic === false ? 'bg-brand-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                Yurtdışı
              </button>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="btn-primary text-xs !w-auto px-3 shrink-0"
                onClick={saveMarket}
                disabled={marketSaving || (editIsDomestic !== true && editIsDomestic !== false)}
              >
                {marketSaving ? '…' : 'Kaydet'}
              </button>
              <button
                type="button"
                className="btn-secondary text-xs !w-auto px-3 shrink-0"
                onClick={() => setEditingMarket(false)}
              >
                İptal
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-gray-800">
              Piyasa:{' '}
              {customer.is_domestic === true && 'Yurtiçi'}
              {customer.is_domestic === false && 'Yurtdışı'}
              {customer.is_domestic !== true && customer.is_domestic !== false && 'Belirtilmemiş'}
            </span>
            {isAdmin && (
              <button
                type="button"
                title="Piyasayı düzenle"
                className="p-1 text-gray-400 hover:text-brand-600 shrink-0"
                onClick={() => {
                  const d = customer.is_domestic;
                  setEditIsDomestic(d === true ? true : d === false ? false : null);
                  setEditingMarket(true);
                }}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round"
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── İletişim kişileri ── */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-gray-700">İletişim kişileri</h2>
          <button type="button" className="text-xs font-semibold text-brand-600" onClick={() => setShowAddContact(true)}>
            + Kişi Ekle
          </button>
        </div>

        {contacts.length === 0 && <p className="text-xs text-gray-400">Henüz iletişim kişisi yok.</p>}

        <ul className="space-y-2">
          {contacts.map((p) => (
            <li key={p.id} className="card text-sm">
              {/* Düzenleme modu */}
              {editingContactId === p.id ? (
                <ContactForm
                  initial={{ title: p.title, first_name: p.first_name, last_name: p.last_name, phone: p.phone, email: p.email }}
                  onSave={submitEditContact}
                  onCancel={() => { setEditingContactId(null); setEditContactError(''); }}
                  saving={editContactSaving}
                  error={editContactError}
                />
              ) : (
                <>
                  <div className="flex items-start gap-1">
                    <div className="flex-1">
                      {p.title && <p className="text-xs text-gray-500">{p.title}</p>}
                      <p className="font-medium text-gray-900">{p.first_name} {p.last_name}</p>
                    </div>
                    {isAdmin && (
                      <div className="flex gap-2 shrink-0">
                        <button type="button" className="text-xs text-brand-600 font-medium"
                          onClick={() => { setEditingContactId(p.id); setEditContactError(''); }}>
                          Düzenle
                        </button>
                        <button type="button" className="text-xs text-red-500 font-medium"
                          onClick={() => deleteContact(p)}>
                          Sil
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="mt-2 space-y-1 text-xs">
                    {p.phone && <a href={`tel:${p.phone.replace(/\s/g, '')}`} className="text-brand-600 block">{p.phone}</a>}
                    {p.email && <a href={`mailto:${p.email}`} className="text-brand-600 block break-all">{p.email}</a>}
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      </section>

      {/* ── Bu müşteriye ait işler ── */}
      <section>
        <h2 className="text-sm font-semibold text-gray-700 mb-2">Bu müşteriye ait işler</h2>

        {/* Arama */}
        <div className="relative mb-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
          <input
            className="input pl-8 text-sm"
            style={{ fontSize: '16px' }}
            placeholder="Başlık veya süreç notunda ara…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"
              onClick={() => setSearch('')}>✕</button>
          )}
        </div>
        <p className="text-xs text-gray-400 mb-3">
          Kelimeler arasındaki ayraç ({'/'}  – . boşluk) önemsizdir — "100 ton/ay" ile "100 ton-ay" aynı sonucu verir.
        </p>

        {filteredTasks.length === 0 && (
          <p className="text-sm text-gray-400">{search ? 'Eşleşen iş bulunamadı.' : 'İş kaydı yok.'}</p>
        )}

        {/* ── Kategori klasörleri ── */}
        {Object.entries(catTree)
          .sort(([a], [b]) => a.localeCompare(b, 'tr'))
          .map(([catName, yearMap]) => {
            const catTotal = Object.values(yearMap).reduce((s, arr) => s + arr.length, 0);
            const catOpen  = openFolders.has(catName);
            return (
              <div key={catName} className="mb-1">
                <FolderRow
                  label={catName}
                  count={catTotal}
                  open={catOpen}
                  onClick={() => toggleFolder(catName)}
                />

                {catOpen && (
                  <div className="ml-2">
                    {Object.entries(yearMap)
                      .sort(([a], [b]) => Number(b) - Number(a))
                      .map(([year, yearTasks]) => {
                        const yearKey  = `${catName}|||${year}`;
                        const yearOpen = openFolders.has(yearKey);
                        return (
                          <div key={year}>
                            <FolderRow
                              label={year}
                              count={yearTasks.length}
                              open={yearOpen}
                              onClick={() => toggleFolder(yearKey)}
                              indent
                            />
                            {yearOpen && (
                              <div className="ml-5 mt-1 space-y-2 mb-2">
                                {yearTasks
                                  .slice()
                                  .sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0))
                                  .map((task) => <TaskCard key={task.id} task={task} />)}
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            );
          })}

        {/* ── TÜMÜ klasörü ── */}
        {filteredTasks.length > 0 && (
          <div className="mb-1">
            <FolderRow
              label="Tümü"
              count={filteredTasks.length}
              open={openFolders.has('__all__')}
              onClick={() => toggleFolder('__all__')}
            />
            {openFolders.has('__all__') && (
              <div className="mt-1 ml-3 space-y-2">
                {pageItems.map((task) => <TaskCard key={task.id} task={task} />)}

                {/* Sayfalama */}
                {totalPages > 1 && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {Array.from({ length: totalPages }, (_, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setPage(i)}
                        className={`w-7 h-7 text-xs rounded-lg font-medium ${
                          i === currentPage
                            ? 'bg-brand-500 text-white'
                            : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </section>

      {/* ── Kişi ekleme modalı ── */}
      {showAddContact && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/40 sm:items-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-4 space-y-3 max-h-[90vh] overflow-y-auto">
            <h3 className="font-semibold text-gray-900">İletişim kişisi ekle</h3>
            <ContactForm
              initial={emptyContact}
              onSave={submitAddContact}
              onCancel={() => { setShowAddContact(false); setAddContactError(''); }}
              saving={addContactSaving}
              error={addContactError}
            />
          </div>
        </div>
      )}
    </div>
  );
}
